import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, Trash2, Mail, User, Shield, 
  Users, Loader2, AlertTriangle, CheckCircle, Plus,
  Pencil, X, Database, BarChart3, Save, Tag, KeyRound, SlidersHorizontal, Eye, EyeOff
} from 'lucide-react';
import { MarketDataReminder } from '../components/MarketDataReminder';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'users', label: 'Utenti', icon: Users },
  { id: 'tariffs', label: 'Tariffe CTE', icon: Database },
  { id: 'market', label: 'Dati Mercato', icon: BarChart3 },
  { id: 'lead-sources', label: 'Provenienza Lead', icon: Tag },
];

const TARIFF_CATEGORIES = [
  { value: 'energia_domestico', label: 'Energia Domestico' },
  { value: 'energia_condominio', label: 'Energia Condominio' },
  { value: 'energia_business', label: 'Energia Business' },
  { value: 'gas_domestico', label: 'Gas Domestico' },
  { value: 'gas_condominio', label: 'Gas Condominio' },
  { value: 'gas_business', label: 'Gas Business' },
];

// Format number to Italian locale for display
const fmtNum = (n, d = 4) => n != null ? Number(n).toLocaleString('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d }) : '';

export default function AdminPage() {
  const { user } = useAuth();
  const location = useLocation();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.some(x => x.id === t) ? t : 'users';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showMsg = useCallback((type, msg) => {
    if (type === 'error') setError(msg);
    else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F7F4]">
      <DashboardHeader section={{ icon: Shield, label: 'Pannello Admin', hint: 'Gestione utenti, tariffe e dati' }} />

      <main className="max-w-6xl mx-auto px-6 py-8">
        <MarketDataReminder variant="admin" />

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" /><p>{error}</p>
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-600 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" /><p>{success}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#4CAF50] text-white shadow-md'
                  : 'bg-white text-[#5C6E5C] border border-[#DCE3DC] hover:bg-[#F4F7F4]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'users' && <UsersTab showMsg={showMsg} currentUserId={user?.id} />}
        {activeTab === 'tariffs' && <TariffsTab showMsg={showMsg} />}
        {activeTab === 'market' && <MarketDataTab showMsg={showMsg} />}
        {activeTab === 'lead-sources' && <LeadSourcesTab showMsg={showMsg} />}
      </main>
    </div>
  );
}

// ===================== USERS TAB =====================
function UsersTab({ showMsg, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'agent' });
  const [submitting, setSubmitting] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [showNewUserPwd, setShowNewUserPwd] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => setUsers(r.data))
      .catch(() => showMsg('error', 'Errore caricamento utenti'))
      .finally(() => setLoading(false));
  }, [showMsg]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/users`, formData, { withCredentials: true });
      setUsers(prev => [...prev, data]);
      setFormData({ email: '', password: '', name: '', role: 'agent' });
      setShowForm(false);
      showMsg('success', `Utente ${data.name} creato`);
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore creazione utente');
    }
    setSubmitting(false);
  };

  const handleResetPassword = (id, name) => setResetUser({ id, name });

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare l'utente ${name}?`)) return;
    try {
      await axios.delete(`${API}/users/${id}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.id !== id));
      showMsg('success', `Utente ${name} eliminato`);
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const getRoleLabel = (role) => ({ admin: 'Amministratore', employee: 'Dipendente' }[role] || 'Agente');
  const getRoleColor = (role) => ({ admin: 'bg-red-100 text-red-600', employee: 'bg-blue-100 text-blue-600' }[role] || 'bg-green-100 text-green-600');

  if (loading) return <div className="el-card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>;

  return (
    <div className="space-y-6">
      <div className="el-card p-6">
        {!showForm ? (
          <button onClick={() => setShowForm(true)} className="el-btn-primary flex items-center gap-2" data-testid="add-user-btn">
            <UserPlus className="w-5 h-5" /> Nuovo Utente
          </button>
        ) : (
          <form onSubmit={handleCreate} className="space-y-4">
            <h3 className="text-lg font-medium text-[#1B291B]">Crea Nuovo Utente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="el-label block mb-2">Nome</label>
                <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="el-input w-full" placeholder="Nome completo" required data-testid="new-user-name" />
              </div>
              <div>
                <label className="el-label block mb-2">Email</label>
                <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="el-input w-full" placeholder="email@esempio.it" required data-testid="new-user-email" />
              </div>
              <div>
                <label className="el-label block mb-2">Password</label>
                <div className="relative">
                  <input type={showNewUserPwd ? 'text' : 'password'} value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                    className="el-input w-full pr-10" placeholder="Minimo 6 caratteri" required minLength={6} data-testid="new-user-password" />
                  <button type="button" onClick={() => setShowNewUserPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6E5C] hover:text-[#1B291B]"
                    title={showNewUserPwd ? 'Nascondi password' : 'Mostra password'} data-testid="toggle-new-user-password">
                    {showNewUserPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="el-label block mb-2">Ruolo</label>
                <select value={formData.role} onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                  className="el-input w-full" data-testid="new-user-role">
                  <option value="agent">Agente</option>
                  <option value="employee">Dipendente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting} className="el-btn-primary flex items-center gap-2" data-testid="submit-new-user">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />} Crea Utente
              </button>
              <button type="button" onClick={() => { setShowForm(false); setFormData({ email: '', password: '', name: '', role: 'agent' }); }}
                className="el-btn-secondary">Annulla</button>
            </div>
          </form>
        )}
      </div>

      <div className="el-card overflow-hidden">
        <div className="p-4 border-b border-[#DCE3DC] bg-[#F4F7F4]">
          <h3 className="font-medium text-[#1B291B]">Utenti Registrati ({users.length})</h3>
        </div>
        <div className="divide-y divide-[#DCE3DC]" data-testid="users-list">
          {users.map(u => (
            <div key={u.id} className="p-4 flex items-center justify-between hover:bg-[#F4F7F4] transition-colors" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  {u.role === 'admin' ? <Shield className="w-4 h-4 text-red-500" /> : <User className="w-4 h-4 text-[#4CAF50]" />}
                </div>
                <div>
                  <p className="font-medium text-[#1B291B]">{u.name}</p>
                  <div className="flex items-center gap-2 text-sm text-[#5C6E5C]"><Mail className="w-3 h-3" /><span>{u.email}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${getRoleColor(u.role)}`}>{getRoleLabel(u.role)}</span>
                {u.role !== 'admin' && (u.allowed_customer_types != null || u.allowed_cte_codes != null) && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700" data-testid={`perm-badge-${u.id}`}>Limitato</span>
                )}
                {u.role !== 'admin' && (
                  <button onClick={() => setPermUser(u)} className="p-2 rounded-lg text-[#5C6E5C] hover:bg-[#F4F7F4] transition-colors" title="Permessi (tipologie e CTE)" data-testid={`permissions-${u.id}`}>
                    <SlidersHorizontal className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => handleResetPassword(u.id, u.name)} className="p-2 rounded-lg text-[#4CAF50] hover:bg-[#E8F5E9] transition-colors" title="Reset password" data-testid={`reset-password-${u.id}`}>
                  <KeyRound className="w-4 h-4" />
                </button>
                {u.id !== currentUserId && (
                  <button onClick={() => handleDelete(u.id, u.name)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {permUser && (
        <PermissionsModal
          user={permUser}
          onClose={() => setPermUser(null)}
          showMsg={showMsg}
          onSaved={(updated) => {
            setUsers(prev => prev.map(x => x.id === updated.id ? updated : x));
            setPermUser(null);
          }}
        />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
          showMsg={showMsg}
        />
      )}
    </div>
  );
}

// ===================== RESET PASSWORD MODAL (Admin) =====================
function ResetPasswordModal({ user, onClose, showMsg }) {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (pwd.length < 6) { setErr('La password deve avere almeno 6 caratteri'); return; }
    if (pwd !== confirm) { setErr('Le password non coincidono'); return; }
    setErr('');
    setSaving(true);
    try {
      await axios.put(`${API}/users/${user.id}/password`, { new_password: pwd }, { withCredentials: true });
      showMsg('success', `Password aggiornata per ${user.name}`);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore reset password');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="reset-password-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-medium text-[#1B291B] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4CAF50]" /> Reset Password</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F7F4] rounded-lg" data-testid="reset-cancel-btn"><X className="w-5 h-5 text-[#5C6E5C]" /></button>
        </div>
        <p className="text-sm text-[#5C6E5C] mb-5">Imposta una nuova password per <span className="font-medium text-[#1B291B]">{user.name}</span>.</p>
        {err && <div className="mb-4 p-3 rounded-xl text-sm bg-red-50 text-red-600 border border-red-200" data-testid="reset-error">{err}</div>}
        <div className="space-y-4">
          <div className="relative">
            <input type={show ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)}
              className="el-input w-full pr-10" placeholder="Nuova password (min. 6)" data-testid="reset-new-password" autoFocus />
            <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6E5C] hover:text-[#1B291B]" data-testid="reset-toggle-visibility">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="relative">
            <input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="el-input w-full pr-10" placeholder="Conferma nuova password" data-testid="reset-confirm-password" />
          </div>
          <div className="flex gap-3 justify-end pt-1">
            <button onClick={onClose} className="el-btn-secondary">Annulla</button>
            <button onClick={handleSave} disabled={saving || !pwd || !confirm} className="el-btn-primary flex items-center gap-2" data-testid="reset-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Aggiorna Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== PERMISSIONS MODAL =====================
const ALL_CUSTOMER_TYPES = [
  { value: 'domestico', label: 'Domestico' },
  { value: 'condominio', label: 'Condominio' },
  { value: 'business', label: 'Business' },
];
const SERVICES = [
  { key: 'energia', label: 'Energia' },
  { key: 'gas', label: 'Gas' },
];

function PermissionsModal({ user, onClose, onSaved, showMsg }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tariffs, setTariffs] = useState({});
  const [restrictEnabled, setRestrictEnabled] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [selectedCtes, setSelectedCtes] = useState(new Set());

  const codesForTypes = useCallback((typesSet, tf = tariffs) => {
    const out = [];
    typesSet.forEach((ct) => {
      SERVICES.forEach((s) => {
        (tf[`${s.key}_${ct}`] || []).forEach((t) => out.push(t.code));
      });
    });
    return out;
  }, [tariffs]);

  useEffect(() => {
    axios.get(`${API}/admin/tariffs`, { withCredentials: true })
      .then((r) => {
        const tf = r.data || {};
        setTariffs(tf);
        const hasRestriction = user.allowed_customer_types != null || user.allowed_cte_codes != null;
        const types = Array.isArray(user.allowed_customer_types)
          ? new Set(user.allowed_customer_types)
          : new Set(ALL_CUSTOMER_TYPES.map((t) => t.value));
        setRestrictEnabled(hasRestriction);
        setSelectedTypes(types);
        if (Array.isArray(user.allowed_cte_codes)) {
          setSelectedCtes(new Set(user.allowed_cte_codes));
        } else {
          setSelectedCtes(new Set(codesForTypes(types, tf)));
        }
      })
      .catch(() => showMsg('error', 'Errore caricamento tariffe'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleType = (ct) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      const catCodes = [];
      SERVICES.forEach((s) => (tariffs[`${s.key}_${ct}`] || []).forEach((t) => catCodes.push(t.code)));
      if (next.has(ct)) {
        next.delete(ct);
        setSelectedCtes((pc) => { const n = new Set(pc); catCodes.forEach((c) => n.delete(c)); return n; });
      } else {
        next.add(ct);
        setSelectedCtes((pc) => { const n = new Set(pc); catCodes.forEach((c) => n.add(c)); return n; });
      }
      return next;
    });
  };

  const toggleCte = (code) => {
    setSelectedCtes((prev) => { const n = new Set(prev); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  };

  const toggleCategoryAll = (codes) => {
    const allSel = codes.every((c) => selectedCtes.has(c));
    setSelectedCtes((prev) => { const n = new Set(prev); codes.forEach((c) => allSel ? n.delete(c) : n.add(c)); return n; });
  };

  const handleSave = async () => {
    let payload;
    if (!restrictEnabled) {
      payload = { allowed_customer_types: null, allowed_cte_codes: null };
    } else {
      const types = ALL_CUSTOMER_TYPES.map((t) => t.value).filter((v) => selectedTypes.has(v));
      if (types.length === 0) { showMsg('error', 'Seleziona almeno una tipologia cliente'); return; }
      const allowedCategoryCodes = codesForTypes(new Set(types));
      const checked = allowedCategoryCodes.filter((c) => selectedCtes.has(c));
      if (checked.length === 0) { showMsg('error', 'Seleziona almeno una CTE'); return; }
      payload = {
        allowed_customer_types: types,
        allowed_cte_codes: checked.length === allowedCategoryCodes.length ? null : checked,
      };
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/users/${user.id}/permissions`, payload, { withCredentials: true });
      showMsg('success', `Permessi aggiornati per ${user.name}`);
      onSaved(data);
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore salvataggio permessi');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="perm-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
        <div className="p-5 border-b border-[#DCE3DC] flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-[#1B291B]">Permessi — {user.name}</h3>
            <p className="text-sm text-[#5C6E5C]">Limita tipologie cliente e CTE visibili/utilizzabili</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F7F4] rounded-lg" data-testid="perm-cancel-btn">
            <X className="w-5 h-5 text-[#5C6E5C]" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>
        ) : (
          <div className="p-5 overflow-y-auto space-y-5">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="perm-restrict-toggle">
              <input type="checkbox" checked={restrictEnabled} onChange={(e) => setRestrictEnabled(e.target.checked)}
                className="w-5 h-5 accent-[#4CAF50]" />
              <span className="font-medium text-[#1B291B]">Limita accesso di questo account</span>
            </label>

            {!restrictEnabled ? (
              <div className="p-4 bg-[#F4F7F4] rounded-xl text-sm text-[#5C6E5C]">
                Accesso completo: questo account può usare tutte le tipologie cliente e tutte le CTE.
              </div>
            ) : (
              <>
                <div>
                  <h4 className="el-label mb-2">Tipologie cliente consentite</h4>
                  <div className="flex flex-wrap gap-2">
                    {ALL_CUSTOMER_TYPES.map((ct) => (
                      <label key={ct.value}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTypes.has(ct.value) ? 'border-[#4CAF50] bg-[#E8F5E9]' : 'border-[#DCE3DC]'
                        }`}
                        data-testid={`perm-type-${ct.value}`}>
                        <input type="checkbox" checked={selectedTypes.has(ct.value)} onChange={() => toggleType(ct.value)}
                          className="w-4 h-4 accent-[#4CAF50]" />
                        <span className="text-sm font-medium text-[#1B291B]">{ct.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="el-label">CTE consentite</h4>
                  {ALL_CUSTOMER_TYPES.filter((ct) => selectedTypes.has(ct.value)).map((ct) => (
                    SERVICES.map((s) => {
                      const cat = `${s.key}_${ct.value}`;
                      const list = tariffs[cat] || [];
                      if (list.length === 0) return null;
                      const codes = list.map((t) => t.code);
                      const allSel = codes.every((c) => selectedCtes.has(c));
                      return (
                        <div key={cat} className="border border-[#DCE3DC] rounded-xl overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-2 bg-[#F4F7F4]">
                            <span className="text-sm font-medium text-[#1B291B]">{s.label} {ct.label}</span>
                            <button onClick={() => toggleCategoryAll(codes)}
                              className="text-xs font-medium text-[#4CAF50] hover:underline" data-testid={`perm-cat-all-${cat}`}>
                              {allSel ? 'Deseleziona tutte' : 'Seleziona tutte'}
                            </button>
                          </div>
                          <div className="p-3 flex flex-wrap gap-2">
                            {list.map((t) => (
                              <label key={t.code}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-sm transition-all ${
                                  selectedCtes.has(t.code) ? 'border-[#4CAF50] bg-[#E8F5E9] text-[#1B291B]' : 'border-[#DCE3DC] text-[#5C6E5C]'
                                }`}
                                data-testid={`perm-cte-${t.code}`}>
                                <input type="checkbox" checked={selectedCtes.has(t.code)} onChange={() => toggleCte(t.code)}
                                  className="w-3.5 h-3.5 accent-[#4CAF50]" />
                                {t.code}
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="p-4 border-t border-[#DCE3DC] flex gap-3 justify-end">
          <button onClick={onClose} className="el-btn-secondary">Annulla</button>
          <button onClick={handleSave} disabled={saving || loading} className="el-btn-primary flex items-center gap-2" data-testid="perm-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva Permessi
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ===================== TARIFFS TAB =====================
function TariffsTab({ showMsg }) {
  const [tariffs, setTariffs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('energia_domestico');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ code: '', quota_fissa: '', spread: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadTariffs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tariffs`, { withCredentials: true });
      setTariffs(data);
    } catch {
      showMsg('error', 'Errore caricamento tariffe');
    }
    setLoading(false);
  }, [showMsg]);

  useEffect(() => { loadTariffs(); }, [loadTariffs]);

  const currentTariffs = tariffs[selectedCategory] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/tariffs/${editingId}`, {
          category: selectedCategory, ...formData,
          quota_fissa: parseFloat(formData.quota_fissa),
          spread: parseFloat(formData.spread),
        }, { withCredentials: true });
        showMsg('success', `Tariffa ${formData.code} aggiornata`);
      } else {
        await axios.post(`${API}/admin/tariffs`, {
          category: selectedCategory,
          code: formData.code,
          quota_fissa: parseFloat(formData.quota_fissa),
          spread: parseFloat(formData.spread),
        }, { withCredentials: true });
        showMsg('success', `Tariffa ${formData.code} creata`);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ code: '', quota_fissa: '', spread: '' });
      await loadTariffs();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore salvataggio tariffa');
    }
    setSubmitting(false);
  };

  const handleEdit = (tariff) => {
    setEditingId(tariff.id);
    setFormData({ code: tariff.code, quota_fissa: String(tariff.quota_fissa), spread: String(tariff.spread) });
    setShowForm(true);
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Eliminare la tariffa ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/tariffs/${id}`, { withCredentials: true });
      showMsg('success', `Tariffa ${code} eliminata`);
      await loadTariffs();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  if (loading) return <div className="el-card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>;

  return (
    <div className="space-y-6">
      {/* Category selector */}
      <div className="el-card p-4">
        <div className="flex flex-wrap gap-2" data-testid="tariff-categories">
          {TARIFF_CATEGORIES.map(cat => (
            <button key={cat.value} onClick={() => { setSelectedCategory(cat.value); setShowForm(false); setEditingId(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === cat.value ? 'bg-[#4CAF50] text-white' : 'bg-[#F4F7F4] text-[#5C6E5C] hover:bg-[#E8F5E9]'
              }`}
              data-testid={`cat-${cat.value}`}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add/Edit form */}
      <div className="el-card p-6">
        {!showForm ? (
          <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ code: '', quota_fissa: '', spread: '' }); }}
            className="el-btn-primary flex items-center gap-2" data-testid="add-tariff-btn">
            <Plus className="w-5 h-5" /> Nuova Tariffa
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#1B291B]">{editingId ? 'Modifica' : 'Nuova'} Tariffa</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="p-1 hover:bg-[#F4F7F4] rounded-lg">
                <X className="w-5 h-5 text-[#5C6E5C]" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="el-label block mb-2">Codice</label>
                <input type="text" value={formData.code} onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                  className="el-input w-full" placeholder="es. GC10" required data-testid="tariff-code-input" />
              </div>
              <div>
                <label className="el-label block mb-2">Quota Fissa (EUR/mese)</label>
                <input type="number" step="0.01" value={formData.quota_fissa} onChange={e => setFormData(p => ({ ...p, quota_fissa: e.target.value }))}
                  className="el-input w-full" placeholder="es. 10" required data-testid="tariff-quota-input" />
              </div>
              <div>
                <label className="el-label block mb-2">Spread (EUR)</label>
                <input type="number" step="0.0001" value={formData.spread} onChange={e => setFormData(p => ({ ...p, spread: e.target.value }))}
                  className="el-input w-full" placeholder="es. 0.08" required data-testid="tariff-spread-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting} className="el-btn-primary flex items-center gap-2" data-testid="save-tariff-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Salva Modifiche' : 'Crea Tariffa'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="el-btn-secondary">Annulla</button>
            </div>
          </form>
        )}
      </div>

      {/* Tariffs list */}
      <div className="el-card overflow-hidden">
        <div className="p-4 border-b border-[#DCE3DC] bg-[#F4F7F4]">
          <h3 className="font-medium text-[#1B291B]">
            {TARIFF_CATEGORIES.find(c => c.value === selectedCategory)?.label} ({currentTariffs.length} tariffe)
          </h3>
        </div>
        {currentTariffs.length === 0 ? (
          <div className="p-8 text-center text-[#5C6E5C]">Nessuna tariffa per questa categoria</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tariffs-table">
              <thead>
                <tr className="border-b border-[#DCE3DC] bg-[#F4F7F4]">
                  <th className="text-left p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Codice</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Quota Fissa</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Spread</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DCE3DC]">
                {currentTariffs.map(t => (
                  <tr key={t.id} className="hover:bg-[#F4F7F4] transition-colors" data-testid={`tariff-row-${t.id}`}>
                    <td className="p-3 font-medium text-[#1B291B]">{t.code}</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(t.quota_fissa, 2)} EUR</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(t.spread, 4)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(t)} className="p-1.5 rounded-lg text-[#4CAF50] hover:bg-[#E8F5E9] transition-colors" data-testid={`edit-tariff-${t.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(t.id, t.code)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-tariff-${t.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== MARKET DATA TAB =====================
function MarketDataTab({ showMsg }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ period: '', pun: '', psv: '', dispacciamento: '', capacity: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const { data: result } = await axios.get(`${API}/admin/market-data`, { withCredentials: true });
      setData(result);
    } catch {
      showMsg('error', 'Errore caricamento dati mercato');
    }
    setLoading(false);
  }, [showMsg]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        period: formData.period,
        pun: parseFloat(formData.pun),
        psv: parseFloat(formData.psv),
        dispacciamento: parseFloat(formData.dispacciamento),
        capacity: parseFloat(formData.capacity),
      };
      if (editingId) {
        await axios.put(`${API}/admin/market-data/${editingId}`, payload, { withCredentials: true });
        showMsg('success', `Dati ${formData.period} aggiornati`);
      } else {
        await axios.post(`${API}/admin/market-data`, payload, { withCredentials: true });
        showMsg('success', `Dati ${formData.period} creati`);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ period: '', pun: '', psv: '', dispacciamento: '', capacity: '' });
      await loadData();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore salvataggio');
    }
    setSubmitting(false);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      period: item.period,
      pun: String(item.pun),
      psv: String(item.psv),
      dispacciamento: String(item.dispacciamento),
      capacity: String(item.capacity),
    });
    setShowForm(true);
  };

  const handleDelete = async (id, period) => {
    if (!window.confirm(`Eliminare i dati del periodo ${period}?`)) return;
    try {
      await axios.delete(`${API}/admin/market-data/${id}`, { withCredentials: true });
      showMsg('success', `Dati ${period} eliminati`);
      await loadData();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  // Generate next period suggestion
  const getNextPeriod = () => {
    if (data.length === 0) return '';
    const last = data[data.length - 1].period;
    const [y, m] = last.split('-').map(Number);
    const nextM = m === 12 ? 1 : m + 1;
    const nextY = m === 12 ? y + 1 : y;
    return `${nextY}-${String(nextM).padStart(2, '0')}`;
  };

  if (loading) return <div className="el-card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>;

  return (
    <div className="space-y-6">
      {/* Add/Edit form */}
      <div className="el-card p-6">
        {!showForm ? (
          <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ period: getNextPeriod(), pun: '', psv: '', dispacciamento: '', capacity: '' }); }}
            className="el-btn-primary flex items-center gap-2" data-testid="add-market-data-btn">
            <Plus className="w-5 h-5" /> Nuovo Periodo
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#1B291B]">{editingId ? 'Modifica' : 'Nuovo'} Periodo</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="p-1 hover:bg-[#F4F7F4] rounded-lg">
                <X className="w-5 h-5 text-[#5C6E5C]" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="el-label block mb-2">Periodo (YYYY-MM)</label>
                <input type="text" value={formData.period} onChange={e => setFormData(p => ({ ...p, period: e.target.value }))}
                  className="el-input w-full" placeholder="es. 2026-03" pattern="\d{4}-\d{2}" required disabled={!!editingId}
                  data-testid="market-period-input" />
              </div>
              <div>
                <label className="el-label block mb-2">PUN</label>
                <input type="number" step="0.00001" value={formData.pun} onChange={e => setFormData(p => ({ ...p, pun: e.target.value }))}
                  className="el-input w-full" placeholder="es. 0.13699" required data-testid="market-pun-input" />
              </div>
              <div>
                <label className="el-label block mb-2">PSV</label>
                <input type="number" step="0.000001" value={formData.psv} onChange={e => setFormData(p => ({ ...p, psv: e.target.value }))}
                  className="el-input w-full" placeholder="es. 0.404227" required data-testid="market-psv-input" />
              </div>
              <div>
                <label className="el-label block mb-2">Dispacciamento</label>
                <input type="number" step="0.00001" value={formData.dispacciamento} onChange={e => setFormData(p => ({ ...p, dispacciamento: e.target.value }))}
                  className="el-input w-full" placeholder="es. 0.00969" required data-testid="market-disp-input" />
              </div>
              <div>
                <label className="el-label block mb-2">Capacity Market</label>
                <input type="number" step="0.00001" value={formData.capacity} onChange={e => setFormData(p => ({ ...p, capacity: e.target.value }))}
                  className="el-input w-full" placeholder="es. 0.01235" required data-testid="market-cap-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting} className="el-btn-primary flex items-center gap-2" data-testid="save-market-data-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Salva Modifiche' : 'Crea Periodo'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="el-btn-secondary">Annulla</button>
            </div>
          </form>
        )}
      </div>

      {/* Market data table */}
      <div className="el-card overflow-hidden">
        <div className="p-4 border-b border-[#DCE3DC] bg-[#F4F7F4]">
          <h3 className="font-medium text-[#1B291B]">Dati di Mercato ({data.length} periodi)</h3>
        </div>
        {data.length === 0 ? (
          <div className="p-8 text-center text-[#5C6E5C]">Nessun dato di mercato</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="market-data-table">
              <thead>
                <tr className="border-b border-[#DCE3DC] bg-[#F4F7F4]">
                  <th className="text-left p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Periodo</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">PUN</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">PSV</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Dispacciamento</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Capacity Market</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DCE3DC]">
                {data.map(d => (
                  <tr key={d.id} className="hover:bg-[#F4F7F4] transition-colors" data-testid={`market-row-${d.id}`}>
                    <td className="p-3 font-medium text-[#1B291B]">{d.period}</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(d.pun, 5)}</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(d.psv, 6)}</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(d.dispacciamento, 5)}</td>
                    <td className="p-3 text-right text-[#1B291B]">{fmtNum(d.capacity, 5)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(d)} className="p-1.5 rounded-lg text-[#4CAF50] hover:bg-[#E8F5E9] transition-colors" data-testid={`edit-market-${d.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(d.id, d.period)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-market-${d.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ===================== LEAD SOURCES TAB =====================
function LeadSourcesTab({ showMsg }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ label: '', order: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lead-sources`, { withCredentials: true });
      setItems(data);
    } catch {
      showMsg('error', 'Errore caricamento provenienze');
    }
    setLoading(false);
  }, [showMsg]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/lead-sources/${editingId}`, {
          label: formData.label,
          ...(formData.order !== '' ? { order: parseInt(formData.order, 10) } : {}),
        }, { withCredentials: true });
        showMsg('success', `Provenienza "${formData.label}" aggiornata`);
      } else {
        await axios.post(`${API}/admin/lead-sources`, {
          label: formData.label,
          ...(formData.order !== '' ? { order: parseInt(formData.order, 10) } : {}),
        }, { withCredentials: true });
        showMsg('success', `Provenienza "${formData.label}" creata`);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ label: '', order: '' });
      await load();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore salvataggio');
    }
    setSubmitting(false);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ label: item.label, order: String(item.order || '') });
    setShowForm(true);
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Eliminare la provenienza "${label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/lead-sources/${id}`, { withCredentials: true });
      showMsg('success', `Provenienza "${label}" eliminata`);
      await load();
    } catch (e) {
      showMsg('error', e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  if (loading) return <div className="el-card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>;

  return (
    <div className="space-y-6">
      <div className="el-card p-6">
        {!showForm ? (
          <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ label: '', order: '' }); }}
            className="el-btn-primary flex items-center gap-2" data-testid="add-lead-source-btn">
            <Plus className="w-5 h-5" /> Nuova Provenienza
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#1B291B]">{editingId ? 'Modifica' : 'Nuova'} Provenienza</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="p-1 hover:bg-[#F4F7F4] rounded-lg">
                <X className="w-5 h-5 text-[#5C6E5C]" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="el-label block mb-2">Nome visualizzato</label>
                <input type="text" value={formData.label} onChange={e => setFormData(p => ({ ...p, label: e.target.value }))}
                  className="el-input w-full" placeholder="es. Agenzia Rossi Milano" required data-testid="lead-source-label-input" />
              </div>
              <div>
                <label className="el-label block mb-2">Ordine (opzionale)</label>
                <input type="number" min="1" value={formData.order} onChange={e => setFormData(p => ({ ...p, order: e.target.value }))}
                  className="el-input w-full" placeholder="lascia vuoto per aggiungere in fondo" data-testid="lead-source-order-input" />
              </div>
            </div>
            {!editingId && (
              <p className="text-xs text-[#5C6E5C]">
                La chiave interna verrà generata automaticamente dal nome (es. &ldquo;Agenzia Rossi Milano&rdquo; &rarr; <code>agenzia_rossi_milano</code>). Non modificabile in seguito.
              </p>
            )}
            <div className="flex gap-3">
              <button type="submit" disabled={submitting} className="el-btn-primary flex items-center gap-2" data-testid="save-lead-source-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Salva Modifiche' : 'Crea Provenienza'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="el-btn-secondary">Annulla</button>
            </div>
          </form>
        )}
      </div>

      <div className="el-card overflow-hidden">
        <div className="p-4 border-b border-[#DCE3DC] bg-[#F4F7F4]">
          <h3 className="font-medium text-[#1B291B]">Provenienze Lead ({items.length})</h3>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-[#5C6E5C]">Nessuna provenienza configurata</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="lead-sources-table">
              <thead>
                <tr className="border-b border-[#DCE3DC] bg-[#F4F7F4]">
                  <th className="text-left p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Ordine</th>
                  <th className="text-left p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Nome</th>
                  <th className="text-left p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Chiave interna</th>
                  <th className="text-right p-3 text-xs uppercase tracking-wider text-[#5C6E5C] font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DCE3DC]">
                {items.map(item => (
                  <tr key={item.id} className="hover:bg-[#F4F7F4] transition-colors" data-testid={`lead-source-row-${item.id}`}>
                    <td className="p-3 text-[#1B291B]">{item.order}</td>
                    <td className="p-3 font-medium text-[#1B291B]">{item.label}</td>
                    <td className="p-3 text-[#5C6E5C] text-sm"><code>{item.value}</code></td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(item)} className="p-1.5 rounded-lg text-[#4CAF50] hover:bg-[#E8F5E9] transition-colors" data-testid={`edit-lead-source-${item.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id, item.label)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-lead-source-${item.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
